import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaClock } from 'react-icons/fa';
import './index.css';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        AOS.init({ duration: 1000, offset: 100, once: true });
    }, []);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
        if (formErrors[id]) {
            setFormErrors(prev => ({ ...prev, [id]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const errors = {};

        if (!formData.name.trim()) errors.name = 'Name is required.';
        if (!formData.email.trim()) errors.email = 'Email is required.';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = 'Invalid email format.';
        }
        if (!formData.message.trim()) errors.message = 'Message is required.';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setLoading(true);
        try {
            await axios.post(process.env.REACT_APP_BASE_URL + '/api/postQuery', {
                name: formData.name,
                emailAddress: formData.email,
                message: formData.message
            });
            setFormData({ name: '', email: '', phone: '', message: '' });
            setSubmitSuccess(true);
            setTimeout(() => setSubmitSuccess(false), 5000);
        } catch (error) {
            console.error(error);
            alert('Error sending message. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const contactInfo = [
        {
            icon: <FaMapMarkerAlt />,
            title: 'Location',
            details: ['2 Yishun Industrial Street 1', '#03-25 Northpoint Bizhub', 'Singapore 768159']
        },
        {
            icon: <FaEnvelope />,
            title: 'Email',
            details: ['hongyuansg@hotmail.com']
        },
        {
            icon: <FaPhone />,
            title: 'Phone',
            details: ['+65 62626341', '+65 94551268']
        },
        {
            icon: <FaClock />,
            title: 'Business Hours',
            details: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday: 9:00 AM - 1:00 PM']
        }
    ];

    return (
        <div className="contact-page-wrapper">
            {/* Hero Section */}
            <section className="contact-hero">
                <div className="contact-hero-overlay"></div>
                <div className="contact-hero-content" data-aos="fade-up">
                    <h1 className="contact-hero-title">Get In Touch</h1>
                    <p className="contact-hero-subtitle">We'd love to hear from you. Send us a message and we'll respond as soon as possible.</p>
                </div>
            </section>

            {/* Contact Info Cards */}
            <section className="contact-info-section">
                <div className="contact-container">
                    <div className="contact-info-grid">
                        {contactInfo.map((info, index) => (
                            <div key={index} className="contact-info-card" data-aos="fade-up" data-aos-delay={index * 100}>
                                <div className="contact-info-icon">
                                    {info.icon}
                                </div>
                                <h3 className="contact-info-title">{info.title}</h3>
                                {info.details.map((detail, i) => (
                                    <p key={i} className="contact-info-detail">{detail}</p>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="contact-form-section">
                <div className="contact-container">
                    <div className="contact-form-wrapper">
                        <div className="contact-form-container" data-aos="fade-up">
                            <h2 className="contact-form-title">Send Us a Message</h2>
                            <form className="contact-form" onSubmit={handleSubmit}>
                                <div className="form-row">
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            id="name"
                                            className={`form-input ${formErrors.name ? 'error' : ''}`}
                                            placeholder="Your Name *"
                                            value={formData.name}
                                            onChange={handleChange}
                                        />
                                        {formErrors.name && <span className="form-error">{formErrors.name}</span>}
                                    </div>
                                    <div className="form-group">
                                        <input
                                            type="email"
                                            id="email"
                                            className={`form-input ${formErrors.email ? 'error' : ''}`}
                                            placeholder="Your Email *"
                                            value={formData.email}
                                            onChange={handleChange}
                                        />
                                        {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                                    </div>
                                </div>
                                <div className="form-group">
                                    <input
                                        type="tel"
                                        id="phone"
                                        className="form-input"
                                        placeholder="Your Phone (Optional)"
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="form-group">
                                    <textarea
                                        id="message"
                                        className={`form-input form-textarea ${formErrors.message ? 'error' : ''}`}
                                        placeholder="Your Message *"
                                        rows="6"
                                        value={formData.message}
                                        onChange={handleChange}
                                    />
                                    {formErrors.message && <span className="form-error">{formErrors.message}</span>}
                                </div>
                                {submitSuccess && (
                                    <div className="alert alert-success">
                                        ✓ Thank you! We have received your message and will get back to you soon.
                                    </div>
                                )}
                                <button 
                                    type="submit" 
                                    className="btn-submit"
                                    disabled={loading}
                                >
                                    {loading ? 'Sending...' : 'Send Message'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Map Section */}
            <section className="contact-map-section">
                <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15954.263400566693!2d103.83455523297617!3d1.435674983247176!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da1460eac7ad97%3A0xe7353622d4e32e26!2sSingapore%20768159!5e0!3m2!1sen!2ssg!4v1629792923002!5m2!1sen!2ssg"
                    className="contact-map"
                    allowFullScreen
                    loading="lazy"
                />
            </section>

            {/* Footer */}
            <footer className="contact-footer">
                <div className="contact-container">
                    <small>
                        {new Date().getFullYear()} Copyright © Hong Yuan Construction Pte Ltd, UEN:200922798D
                    </small>
                </div>
            </footer>
        </div>
    );
};

export default ContactPage;
